import axios from 'axios';
import { mostrarMaterialDeTeste } from './materialDeTeste';
import { getApiBase } from '@/config';
import { soJanelaOriginalLigado } from './escolhaDeReabertas';
import { sugestoesDaIALigadas } from './sugestoesDaIA';
import { ambienteDoAplicativo } from './versao';

const baseURL = getApiBase();

export const api = axios.create({
    baseURL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor para adicionar o token de autenticação
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    // "Só a janela original" (relatórios sem as tentativas reabertas): a escolha viaja em todo pedido, e o servidor
    // a aplica só nas leituras de relatório (`filtroDeReabertas.ts`). Vai na consulta, e não num cabeçalho: cabeçalho
    // novo exigiria liberar no CORS, e o aplicativo já instalado é outra origem.
    if (soJanelaOriginalLigado()) config.params = { ...(config.params ?? {}), semReabertas: 1 };
    // "Incluir sugestões da IA" nos relatórios por habilidade (`sugestoesDaIA.ts`; o servidor, `sugestoesNaConta.ts`).
    if (sugestoesDaIALigadas()) config.params = { ...(config.params ?? {}), sugestoes: 1 };
    // A conta de demonstração esconde o material "ZZ TESTE" das listas; ligada neste aparelho, a escolha o mostra (`materialDeTeste.ts`).
    if (mostrarMaterialDeTeste()) config.params = { ...(config.params ?? {}), materialDeTeste: 1 };
    // "Ver como o aluno" (25/10/2026): enquanto a pré-visualização está aberta, todo pedido vai marcado, e o servidor só deixa
    // LER (`middlewares/preVisualizacao.ts`). Na consulta, e não num cabeçalho, pela razão do `semReabertas`.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/previa/')) config.params = { ...(config.params ?? {}), preVisualizacao: 1 };
    // O MODO PROVA (1.4): ao ABRIR a prova (o simulado, o início e o rascunho) e ao entregar, o aplicativo diz quem é — `android/<versionCode>` ou
    // `ios/<build>` —, e o servidor decide se a prova "exigir ambiente protegido" abre (`services/modoProva.ts`). Na
    // consulta, pela mesma razão do `semReabertas`. No navegador não vai nada, e é isso que o servidor recusa.
    const ambiente = ambienteDoAplicativo();
    // 20/10/2026: também ao ler o simulado (`GET /api/simulations/:id`, que traz as questões) e na entrega — no "exigir"
    // o servidor só entrega as questões e só aceita a entrega com o aplicativo (`services/modoProva.ts`).
    if (ambiente && /\/api\/simulations\/\d+(\/(start|draft|responses))?$/.test(config.url ?? '')) config.params = { ...(config.params ?? {}), ambiente };
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Flag para evitar múltiplos refreshes simultâneos
let isRefreshing = false;
/*
 * A FILA DO REFRESH RESOLVE OU REJEITA — nunca fica parada (achado 166). Antes cada assinante só sabia `resolve`, e
 * quem esvaziava a fila era só o caminho de SUCESSO: se a renovação da sessão falhava, as requisições que esperavam na
 * fila nunca terminavam — nenhum erro, nenhum log, um botão travado em "salvando". Agora a falha rejeita cada uma com o
 * erro da renovação, e quem chamou recebe o erro como recebe qualquer outro.
 */
type AssinanteDoRefresh = { resolver: (token: string) => void; rejeitar: (erro: unknown) => void };
let refreshSubscribers: AssinanteDoRefresh[] = [];

function onRefreshed(token: string) {
    refreshSubscribers.forEach((a) => a.resolver(token));
    refreshSubscribers = [];
}

function onRefreshFailed(erro: unknown) {
    refreshSubscribers.forEach((a) => a.rejeitar(erro));
    refreshSubscribers = [];
}

function addRefreshSubscriber(a: AssinanteDoRefresh) {
    refreshSubscribers.push(a);
}

/** Só para o teste (`api.refresh.test.ts`): quantas requisições esperam na fila agora. */
export const naFilaDoRefresh = () => refreshSubscribers.length;

// Interceptor para tratar 401 com retry via refresh token
/**
 * Resposta HTML numa chamada de API é caminho errado, não sucesso.
 *
 * Em produção `VITE_API_URL` é vazio e o prefixo `/api` precisa estar no
 * caminho — é o Nginx que separa a API do SPA. Uma chamada a `/gestao/x` em vez
 * de `/api/gestao/x` não dá 404: o Nginx serve o `index.html` com **HTTP 200**,
 * o axios entrega a string HTML como `data`, e o erro só aparece muito depois,
 * onde alguém lê uma propriedade do que achava ser um objeto.
 *
 * Foi o que aconteceu com as telas da equipe gestora: 25 chamadas sem o
 * prefixo, e o sintoma foi `Cannot read properties of undefined (reading
 * 'convitesPendentes')` no layout — a três camadas da causa.
 *
 * Este interceptor transforma isso num erro imediato, na chamada que o causou,
 * com o caminho no texto. Custa uma comparação de cabeçalho por resposta.
 */
api.interceptors.response.use(
    (response) => {
        const tipo = String(response.headers?.['content-type'] ?? '');
        if (tipo.includes('text/html')) {
            const url = `${response.config?.baseURL ?? ''}${response.config?.url ?? ''}`;
            throw new Error(
                `A chamada a "${url}" devolveu HTML em vez de JSON. ` +
                `Em produção o caminho da API precisa começar com "/api".`
            );
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Troca de senha obrigatória (achado 204): o servidor recusa tudo até a troca.
        if (error.response?.status === 403 && error.response?.data?.codigo === 'SENHA_TROCA_OBRIGATORIA') {
            window.dispatchEvent(new Event('auth:trocaDeSenha'));
            return Promise.reject(error);
        }
        // Termos de uso no primeiro acesso (pedido de acesso): o servidor recusa tudo até o aceite.
        if (error.response?.status === 403 && error.response?.data?.codigo === 'TERMOS_PENDENTES') {
            window.dispatchEvent(new Event('auth:termos'));
            return Promise.reject(error);
        }

        // Se não é 401, ou já tentou retry, ou é a própria rota de refresh/login → falha direto
        if (
            error.response?.status !== 401 ||
            originalRequest._retry ||
            originalRequest.url?.includes('/api/auth/refresh') ||
            originalRequest.url?.includes('/api/auth/login') ||
            // "Trocar de conta" no navegador: o 401 é da conta LEMBRADA (venceu, foi revogada), não da sessão aberta.
            originalRequest.url?.includes('/api/contas-do-navegador')
        ) {
            // Se é 401 final (sem retry possível), dispara logout
            if (error.response?.status === 401 && !originalRequest.url?.includes('/api/auth/login') && !originalRequest.url?.includes('/api/contas-do-navegador')) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.dispatchEvent(new Event('auth:logout'));
            }
            return Promise.reject(error);
        }

        // Marcar como retry
        originalRequest._retry = true;

        // Se já está fazendo refresh, esperar na fila
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                addRefreshSubscriber({
                    resolver: (newToken: string) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        resolve(api(originalRequest));
                    },
                    rejeitar: reject,
                });
            });
        }

        isRefreshing = true;

        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No token');

            const response = await axios.post(
                `${baseURL}/api/auth/refresh`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const newToken = response.data.token;
            const newUser = response.data.user;

            localStorage.setItem('token', newToken);
            localStorage.setItem('user', JSON.stringify(newUser));

            // Notificar AuthContext que o token foi atualizado
            window.dispatchEvent(new CustomEvent('auth:refreshed', { detail: { token: newToken, user: newUser } }));

            // Notificar requests em fila
            onRefreshed(newToken);

            // Repetir a request original com novo token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
        } catch {
            // Refresh falhou — logout, e as requisições que esperavam na fila recebem o MESMO erro (achado 166).
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            onRefreshFailed(error);
            window.dispatchEvent(new Event('auth:logout'));
            return Promise.reject(error);
        } finally {
            isRefreshing = false;
        }
    }
);
